import { Star, User, ChevronLeft, ChevronRight, Quote, ImageOff } from "lucide-react";
import { BeforeAfterSlider } from "./BeforeAfterSlider";
import { useLanguage } from "@/lib/i18n";
import { useCallback, useEffect, useState, useRef } from "react";

export interface Review {
  name: string;
  rating: number;
  text: string;
  image?: string | null;
  reviewImage?: string | null;
  verified?: boolean;
}

interface CustomerReviewsProps {
  reviews: Review[];
  title?: string;
}

export function CustomerReviews({ reviews, title }: CustomerReviewsProps) {
  const { t } = useLanguage();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeIndexRef = useRef(0);
  const [totalDots, setTotalDots] = useState(1);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Calculate how many dots we need based on visible cards per screen
  const getVisibleCount = useCallback(() => {
    if (typeof window === "undefined") return 1;
    if (window.innerWidth >= 1024) return 3;
    if (window.innerWidth >= 768) return 2;
    return 1;
  }, []);

  // Update dot count on resize
  useEffect(() => {
    const updateDots = () => {
      const visible = getVisibleCount();
      setTotalDots(Math.max(1, reviews.length - visible + 1));
    };
    updateDots();
    window.addEventListener("resize", updateDots);
    return () => window.removeEventListener("resize", updateDots);
  }, [reviews.length, getVisibleCount]);

  // Track active index via IntersectionObserver — NO activeIndex in deps!
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        let bestIndex = activeIndexRef.current;
        let bestRatio = 0;
        entries.forEach((entry) => {
          const idx = Number(entry.target.getAttribute("data-index"));
          if (entry.intersectionRatio > bestRatio) {
            bestRatio = entry.intersectionRatio;
            bestIndex = idx;
          }
        });
        if (bestRatio > 0.5) {
          activeIndexRef.current = bestIndex;
          setActiveIndex(bestIndex);
        }
      },
      {
        root: container,
        threshold: 0.5,
      }
    );

    cardRefs.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, [reviews.length]);

  // scrollBy-based navigation — works in both LTR and RTL
  const getCardScrollWidth = useCallback(() => {
    const card = cardRefs.current[0];
    if (!card) return 300;
    // gap is 12px on mobile (gap-3), 16px on desktop (gap-4)
    const gap = window.innerWidth >= 768 ? 16 : 12;
    return card.offsetWidth + gap;
  }, []);

  const scrollPrev = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollBy({ left: -getCardScrollWidth(), behavior: "smooth" });
  }, [getCardScrollWidth]);

  const scrollNext = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollBy({ left: getCardScrollWidth(), behavior: "smooth" });
  }, [getCardScrollWidth]);

  const scrollToIndex = useCallback(
    (index: number) => {
      const card = cardRefs.current[index];
      const container = scrollRef.current;
      if (!card || !container) return;
      card.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
    },
    []
  );

  // Clamp dot index
  const activeDot = Math.min(activeIndex, totalDots - 1);

  return (
    <section className="mb-8 md:mb-10">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-5 md:mb-7">
        {title || t("product.reviews")}
      </h2>

      {/* Carousel container */}
      <div className="relative">
        {/* Navigation arrows — desktop only, always visible */}
        <button
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); scrollPrev(); }}
          className="absolute -left-3 md:-left-5 top-1/2 -translate-y-1/2 z-20 hidden md:flex items-center justify-center w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm border border-white/60 shadow-lg text-foreground/70 hover:text-foreground hover:bg-white hover:shadow-xl transition-all duration-200 cursor-pointer"
          aria-label="Previous review"
          type="button"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); scrollNext(); }}
          className="absolute -right-3 md:-right-5 top-1/2 -translate-y-1/2 z-20 hidden md:flex items-center justify-center w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm border border-white/60 shadow-lg text-foreground/70 hover:text-foreground hover:bg-white hover:shadow-xl transition-all duration-200 cursor-pointer"
          aria-label="Next review"
          type="button"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Scroll Snap container */}
        <div
          ref={scrollRef}
          className="scrollbar-hide flex gap-3 md:gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {reviews.map((review, idx) => {
            const hasAnyReviewImages = reviews.some(r => !!r.reviewImage);
            return (
              <div
                key={idx}
                ref={(el) => { cardRefs.current[idx] = el; }}
                data-index={idx}
                className="snap-start shrink-0 w-[85%] md:w-[48%] lg:w-[31.5%]"
              >
                <ReviewCard review={review} t={t} showImageArea={hasAnyReviewImages} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Dots indicator */}
      {totalDots > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          {Array.from({ length: totalDots }).map((_, idx) => (
            <button
              key={idx}
              onClick={() => scrollToIndex(idx)}
              className={`rounded-full transition-all duration-300 ${
                idx === activeDot
                  ? "w-6 h-2.5 bg-primary shadow-sm"
                  : "w-2.5 h-2.5 bg-primary/25 hover:bg-primary/40"
              }`}
              aria-label={`Go to review ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/* ─── Review Card ─── */

function ReviewCard({
  review,
  t,
  showImageArea,
}: {
  review: Review;
  t: (key: string) => string;
  showImageArea: boolean;
}) {
  return (
    <div className="h-full p-5 md:p-6 bg-white/70 backdrop-blur-sm rounded-2xl border border-white/80 shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col items-center text-center">
      {/* Customer photo — large avatar */}
      <div className="mb-4">
        {review.image ? (
          <img
            src={review.image}
            alt={review.name}
            className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-3 border-secondary shadow-md"
          />
        ) : (
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-secondary to-pink-200 flex items-center justify-center shadow-md">
            <User className="w-8 h-8 md:w-10 md:h-10 text-primary/50" />
          </div>
        )}
      </div>

      {/* Name + Verified badge */}
      <div className="flex items-center gap-2 flex-wrap justify-center mb-1">
        <p className="font-semibold text-foreground">{review.name}</p>
        {review.verified && (
          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
            ✓ {t("common.verified")}
          </span>
        )}
      </div>

      {/* Stars */}
      <div className="flex gap-0.5 justify-center mb-4">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`w-4 h-4 ${
              i < review.rating
                ? "fill-amber-400 text-amber-400"
                : "text-gray-200"
            }`}
          />
        ))}
      </div>

      {/* Quote text */}
      <div className="relative flex-1">
        <Quote className="absolute -top-1 -left-1 w-5 h-5 text-primary/15 rotate-180" />
        <p className="text-muted-foreground text-sm md:text-base leading-relaxed px-4">
          {review.text}
        </p>
      </div>

      {/* Review image area — only shown when the product has any review images */}
      {showImageArea && (
        <div className="mt-4 w-full px-2">
          {review.reviewImage ? (
            <img
              src={review.reviewImage}
              alt={`${review.name} review photo`}
              className="w-full max-h-48 object-cover rounded-xl border border-white/60 shadow-sm"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-32 rounded-xl border border-dashed border-muted-foreground/20 bg-muted/30 flex flex-col items-center justify-center gap-1.5">
              <ImageOff className="w-5 h-5 text-muted-foreground/40" />
              <span className="text-xs text-muted-foreground/50">{t('common.noPhotoShared')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Before/After Section ───

interface BeforeAfterItem {
  beforeImage?: string;
  afterImage?: string;
  name?: string;
  duration?: string;
}

interface BeforeAfterSectionProps {
  items: BeforeAfterItem[];
  title: string;
}

export function BeforeAfterSection({ items, title }: BeforeAfterSectionProps) {
  return (
    <section className="mb-8 md:mb-10">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-5 md:mb-7">
        {title}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {items.map((item, idx) => (
          <div
            key={idx}
            className="bg-white/70 rounded-2xl border border-white/80 shadow-sm overflow-hidden"
          >
            {/* Comparison slider */}
            {item.beforeImage && item.afterImage ? (
              <BeforeAfterSlider
                beforeImage={item.beforeImage}
                afterImage={item.afterImage}
              />
            ) : (
              <div className="aspect-square bg-gradient-to-br from-secondary to-pink-100 flex items-center justify-center">
                <User className="w-12 h-12 text-primary/30" />
              </div>
            )}

            {(item.name || item.duration) && (
              <div className="p-3 md:p-4 text-center">
                {item.name && <p className="font-medium text-sm">{item.name}</p>}
                {item.duration && (
                  <p className="text-xs text-muted-foreground">
                    {item.duration}
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
